import React, { useState, useEffect } from 'react';
import '../scss/Vibration.scss';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartBar,
    faArrowUp,
    faArrowsAltH,
    faArrowsAlt,
    faCogs,
    faCircleNotch,
    faCog,
    faLink,
    faLayerGroup,
    faArrowsAltV,
    faArrowRight,
    faFilter,
    faRedo,
    faCrown,
    faUser,
    faSyncAlt,
    faSignOutAlt,
    faCaretDown,
    faTimes,
    faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
);

const VibrationMonitoring = () => {
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedCrane, setSelectedCrane] = useState('CRN-001');
    const [dateRange, setDateRange] = useState('week');
    const [showSettings, setShowSettings] = useState(false);
    const [activeAxes, setActiveAxes] = useState({
        hoist: 'all',
        travel: 'all',
        trolley: 'all',
        gearbox: 'all',
        pulley: 'all',
        wheels: 'all',
        hook: 'all'
    });
    const [orgLogo, setOrgLogo] = useState(null);

    const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

    useEffect(() => {
        const fetchOrgLogo = async () => {
          try {
            const token = getAuthToken();
            const organizationId = sessionStorage.getItem('organization_id');
    
            const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
              headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json',
              }
            });
    
            if (response.ok) {
              const data = await response.json();
              if (data.data?.logo_url) { 
                setOrgLogo(data.data.logo_url);
              }
            }
          } catch (error) {
            console.error('Error fetching org logo:', error);
          }
        };
    
        fetchOrgLogo();
      }, []);

    // Sample vibration data
    const vibrationData = {
        timestamps: ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'],

        hoist: {
            x: [1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.1],
            y: [2.5, 2.7, 2.9, 3.1, 3.3, 3.5, 3.7, 3.9, 4.1, 4.3],
            z: [1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9]
        },

        travel: {
            x: [1.8, 1.7, 1.8, 1.9, 2.0, 2.1, 2.0, 1.9, 1.8, 1.7],
            y: [1.2, 1.3, 1.2, 1.3, 1.4, 1.3, 1.2, 1.3, 1.4, 1.3],
            z: [1.5, 1.4, 1.5, 1.6, 1.5, 1.4, 1.5, 1.6, 1.5, 1.4]
        },

        trolley: {
            x: [2.0, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9],
            y: [2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 3.0, 3.1],
            z: [5.8, 6.0, 6.2, 6.4, 6.6, 6.8, 7.0, 7.2, 7.4, 7.6]
        },

        gearbox: {
            x: [1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 2.1, 2.2, 2.3, 2.4],
            y: [2.0, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9],
            z: [3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.9, 4.0, 4.1]
        },

        pulley: {
            x: [2.5, 2.6, 2.7, 2.8, 2.9, 3.0, 3.1, 3.2, 3.3, 3.4],
            y: [3.8, 3.9, 4.0, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7],
            z: [2.0, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9]
        },

        wheels: {
            x: [1.5, 1.6, 1.7, 1.8, 1.9, 2.0, 1.9, 1.8, 1.9, 1.8],
            y: [2.0, 2.1, 2.2, 2.3, 2.4, 2.5, 2.4, 2.3, 2.2, 2.1],
            z: [1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.6, 1.5, 1.4, 1.3]
        },

        hook: {
            x: [0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.2, 1.1, 1.2, 1.1],
            y: [1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.6, 1.5, 1.4, 1.3],
            z: [0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.0, 0.9, 0.8, 0.7]
        },

        overview: {
            timestamps: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
            hoist: [2.1, 2.3, 2.8, 3.2, 3.5, 3.8],
            travel: [1.8, 1.9, 2.1, 2.3, 2.5, 2.7],
            trolley: [3.5, 4.0, 4.8, 5.2, 5.8, 6.4],
            gearbox: [2.5, 2.7, 3.0, 3.3, 3.6, 3.9],
            pulley: [3.2, 3.4, 3.8, 4.2, 4.5, 4.8],
            wheels: [1.8, 1.9, 2.1, 2.3, 2.5, 2.7],
            hook: [1.0, 1.1, 1.3, 1.5, 1.7, 1.9]
        }
    };

    // Get vibration color based on value
    const getVibrationColor = (value) => {
        if (value >= 7.1) return '#e74c3c';
        if (value >= 4.5) return '#f39c12';
        return '#2ecc71';
    };

    // Component status based on latest values
    const getComponentStatus = (component) => {
        const lastY = vibrationData[component].y[vibrationData[component].y.length - 1];
        if (lastY >= 7.1) return { text: 'Critical', className: 'status-danger' };
        if (lastY >= 4.5) return { text: 'Warning', className: 'status-warning' };
        return { text: 'Normal', className: 'status-normal' };
    };

    // Overview chart data
    const overviewChartData = {
        labels: vibrationData.overview.timestamps,
        datasets: [
            {
                label: 'Hoist Motor',
                data: vibrationData.overview.hoist,
                borderColor: '#3498db',
                backgroundColor: '#3498db20',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Travel Motor',
                data: vibrationData.overview.travel,
                borderColor: '#2ecc71',
                backgroundColor: '#2ecc7120',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Trolley Motor',
                data: vibrationData.overview.trolley,
                borderColor: '#e74c3c',
                backgroundColor: '#e74c3c20',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Gearbox',
                data: vibrationData.overview.gearbox,
                borderColor: '#9b59b6',
                backgroundColor: '#9b59b620',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Pulley',
                data: vibrationData.overview.pulley,
                borderColor: '#f39c12',
                backgroundColor: '#f39c1220',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Wheels',
                data: vibrationData.overview.wheels,
                borderColor: '#f1c40f',
                backgroundColor: '#f1c40f20',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            },
            {
                label: 'Hook',
                data: vibrationData.overview.hook,
                borderColor: '#1abc9c',
                backgroundColor: '#1abc9c20',
                tension: 0.3,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 2
            }
        ]
    };

    const overviewChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    usePointStyle: true
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} mm/s`;
                    }
                }
            },
            title: {
                display: true,
                text: 'Combined Vibration Velocity Trend',
                font: { size: 16 }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Vibration Velocity (mm/s RMS)' },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
            },
            x: {
                title: { display: true, text: 'Time' },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
            }
        }
    };

    // Create component chart data
    const createComponentChartData = (component, axis) => {
        const data = vibrationData[component];
        const datasets = [];

        if (axis === 'all' || axis === 'x') {
            datasets.push({
                label: 'X-Axis (Longitudinal)',
                data: data.x,
                borderColor: '#3498db',
                backgroundColor: '#3498db20',
                tension: 0.3,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
                borderWidth: 2,
                hidden: axis !== 'all' && axis !== 'x'
            });
        }

        if (axis === 'all' || axis === 'y') {
            datasets.push({
                label: 'Y-Axis (Vertical)',
                data: data.y,
                borderColor: '#2ecc71',
                backgroundColor: '#2ecc7120',
                tension: 0.3,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
                borderWidth: 2,
                hidden: axis !== 'all' && axis !== 'y'
            });
        }

        if (axis === 'all' || axis === 'z') {
            datasets.push({
                label: 'Z-Axis (Lateral)',
                data: data.z,
                borderColor: '#e74c3c',
                backgroundColor: '#e74c3c20',
                tension: 0.3,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5,
                borderWidth: 2,
                hidden: axis !== 'all' && axis !== 'z'
            });
        }

        return {
            labels: vibrationData.timestamps,
            datasets
        };
    };

    const componentChartOptions = (title) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 20,
                    usePointStyle: true
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} mm/s`;
                    }
                }
            },
            title: {
                display: true,
                text: title,
                font: { size: 16 }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Vibration (mm/s)' },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
            },
            x: {
                title: { display: true, text: 'Time' },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
            }
        }
    });

    // Meter component
    const VibrationMeter = ({ value, label }) => {
        const color = getVibrationColor(value);
        const meterData = {
            datasets: [{
                data: [value, 10 - value],
                backgroundColor: [color, '#f5f5f5'],
                borderWidth: 0
            }]
        };

        const meterOptions = {
            responsive: true,
            maintainAspectRatio: false,
            rotation: -90,
            circumference: 180,
            cutout: '80%',
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false }
            }
        };

        return (
            <div className="vibration-meter">
                <Doughnut data={meterData} options={meterOptions} />
                <div className="meter-value" style={{ color }}>{value.toFixed(1)}</div>
                <div className="meter-label">{label}</div>
            </div>
        );
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            localStorage.removeItem('isLoggedIn');
            sessionStorage.clear();
            setTimeout(() => navigate('/login'), 1500);
        }
    };

    // Component Panel
    const ComponentPanel = ({ name, icon, componentKey }) => {
        const status = getComponentStatus(componentKey);
        const data = vibrationData[componentKey];
        const lastValues = {
            x: data.x[data.x.length - 1],
            y: data.y[data.y.length - 1],
            z: data.z[data.z.length - 1]
        };

        return (
            <div className="component-panel">
                <div className="component-header">
                    <div className="component-name">
                        <FontAwesomeIcon icon={icon} />
                        <span>{name}</span>
                    </div>
                    <div className={`component-status ${status.className}`}>{status.text}</div>
                </div>

                <div className="axis-controls">
                    <button
                        className={`axis-btn ${activeAxes[componentKey] === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveAxes(prev => ({ ...prev, [componentKey]: 'all' }))}
                    >
                        <FontAwesomeIcon icon={faLayerGroup} />
                        All Axes
                    </button>
                    <button
                        className={`axis-btn ${activeAxes[componentKey] === 'x' ? 'active' : ''}`}
                        onClick={() => setActiveAxes(prev => ({ ...prev, [componentKey]: 'x' }))}
                    >
                        <FontAwesomeIcon icon={faArrowsAltH} />
                        X-Axis
                    </button>
                    <button
                        className={`axis-btn ${activeAxes[componentKey] === 'y' ? 'active' : ''}`}
                        onClick={() => setActiveAxes(prev => ({ ...prev, [componentKey]: 'y' }))}
                    >
                        <FontAwesomeIcon icon={faArrowsAltV} />
                        Y-Axis
                    </button>
                    <button
                        className={`axis-btn ${activeAxes[componentKey] === 'z' ? 'active' : ''}`}
                        onClick={() => setActiveAxes(prev => ({ ...prev, [componentKey]: 'z' }))}
                    >
                        <FontAwesomeIcon icon={faArrowRight} />
                        Z-Axis
                    </button>
                </div>

                <div className="combined-chart">
                    <Line
                        data={createComponentChartData(componentKey, activeAxes[componentKey])}
                        options={componentChartOptions(name)}
                    />
                </div>

                <div className="vibration-meters">
                    <VibrationMeter value={lastValues.x} label="X-Axis" />
                    <VibrationMeter value={lastValues.y} label="Y-Axis" />
                    <VibrationMeter value={lastValues.z} label="Z-Axis" />
                </div>
            </div>
        );
    };

    // Settings Panel
    const SettingsPanel = () => {
        if (!showSettings) return null;

        return (
            <>
                <div className="overlay show" onClick={() => setShowSettings(false)}></div>
                <div className="settings-container show">
                    <div className="settings-header">
                        <h3 className="settings-title">Settings</h3>
                        <button className="close-settings" onClick={() => setShowSettings(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="settings-section">
                        <h4 className="section-title">Crane Configuration</h4>
                        <div className="settings-form">
                            <div className="form-group">
                                <label className="form-label">Crane ID</label>
                                <select className="form-control" value={selectedCrane} onChange={(e) => setSelectedCrane(e.target.value)}>
                                    <option>CRN-001</option>
                                    <option>CRN-002</option>
                                    <option>CRN-003</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Site</label>
                                <select className="form-control">
                                    <option>Site A</option>
                                    <option>Site B</option>
                                    <option>Site C</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="settings-section">
                        <h4 className="section-title">Components</h4>
                        <div className="settings-form">
                            <div className="form-group">
                                <label className="form-label">Component</label>
                                <select className="form-control">
                                    <option>LT Drive</option>
                                    <option>Hoist</option>
                                    <option>CT Motor</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Sub-Components</label>
                                <select className="form-control">
                                    <option>Motor</option>
                                    <option>Gearbox</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Monitor Mode</label>
                                <select className="form-control">
                                    <option>Decoupled</option>
                                    <option>Coupled</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button className="btn btn-secondary" onClick={() => setShowSettings(false)}>Cancel</button>
                        <button className="btn btn-primary" onClick={() => {
                            alert('Settings saved successfully!');
                            setShowSettings(false);
                        }}>Save</button>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="vibration-monitoring-container">
            <div className="dashboard-content">
                <div className="header">
                    <div className="header-left">
                        <div className="header-logo">CraneIQ</div>
                    </div>

                    <div className="user-info-wrapper">
                        <div className="account-dropdown">
                            <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                {orgLogo ? (
                                                  <img
                                                    src={orgLogo}
                                                    alt="Organization Logo"
                                                    style={{
                                                      width: '32px',
                                                      height: '32px',
                                                      borderRadius: '50%',
                                                      objectFit: 'cover',
                                                      border: '2px solid #4361ee'
                                                    }}
                                                    onError={() => setOrgLogo(null)}
                                                  />
                                                ) : (
                                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                                )}
                                <span>{sessionStorage.getItem('username') || 'User'}</span>
                                <FontAwesomeIcon icon={faCaretDown} />
                            </div>
                            {isDropdownOpen && (
                                <div className="account-menu show">
                                    <div className="account-menu-item plan-info">
                                        <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                        <span>Basic Plan</span>
                                    </div>
                                    <div className="account-menu-divider"></div>
                                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                        <FontAwesomeIcon icon={faUser} /> Profile
                                    </button>
                                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                        <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                                    </button>
                                    <div className="account-menu-divider"></div>
                                    <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                        <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="page-title">
                    <h1>Vibration Monitoring</h1>
                    <p>Real-time vibration analysis of crane components</p>
                </div>

                <div className="log-controls">
                    <div className="filter-group">
                        <label className="filter-label">Crane</label>
                        <select
                            className="filter-select"
                            value={selectedCrane}
                            onChange={(e) => setSelectedCrane(e.target.value)}
                        >
                            <option value="all">All Cranes</option>
                            <option value="CRN-001">EOT Crane #1 (CRN-001)</option>
                            <option value="CRN-002">EOT Crane #2 (CRN-002)</option>
                            <option value="CRN-003">EOT Crane #3 (CRN-003)</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Date Range</label>
                        <select
                            className="filter-select"
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <option value="today">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="custom">Custom Range</option>
                        </select>
                    </div>

                    <div className="action-buttons">
                        <button className="btn btn-primary">
                            <FontAwesomeIcon icon={faFilter} />
                            Apply Filters
                        </button>
                        <button className="btn btn-secondary">
                            <FontAwesomeIcon icon={faRedo} />
                            Reset
                        </button>
                        <button className="btn btn-primary" onClick={() => setShowSettings(true)}>
                            <FontAwesomeIcon icon={faCog} /> Settings
                        </button>
                    </div>
                </div>

                <div className="overview-panel">
                    <div className="overview-header">
                        <div className="overview-title">
                            <FontAwesomeIcon icon={faChartBar} />
                            <span>Vibration Overview</span>
                        </div>
                        <div className="component-status status-monitoring">Monitoring</div>
                    </div>

                    <div className="overview-chart">
                        <Line data={overviewChartData} options={overviewChartOptions} />
                    </div>

                    <div className="parameter-cards">
                        <div className="parameter-card">
                            <div className="parameter-label">Peak Acceleration</div>
                            <div className="parameter-value">2.8</div>
                            <div className="parameter-unit">m/s² RMS</div>
                        </div>
                        <div className="parameter-card">
                            <div className="parameter-label">Velocity</div>
                            <div className="parameter-value">4.2</div>
                            <div className="parameter-unit">mm/s RMS</div>
                        </div>
                        <div className="parameter-card">
                            <div className="parameter-label">Displacement</div>
                            <div className="parameter-value">0.12</div>
                            <div className="parameter-unit">mm P-P</div>
                        </div>
                        <div className="parameter-card">
                            <div className="parameter-label">Dominant Frequency</div>
                            <div className="parameter-value">12.5</div>
                            <div className="parameter-unit">Hz</div>
                        </div>
                    </div>
                </div>

                <div className="components-grid">
                    <ComponentPanel name="Hoist Motor" icon={faArrowUp} componentKey="hoist" />
                    <ComponentPanel name="Travel Motor" icon={faArrowsAltH} componentKey="travel" />
                    <ComponentPanel name="Trolley Motor" icon={faArrowsAlt} componentKey="trolley" />
                    <ComponentPanel name="Gearbox" icon={faCogs} componentKey="gearbox" />
                    <ComponentPanel name="Pulley" icon={faCircleNotch} componentKey="pulley" />
                    <ComponentPanel name="Wheels" icon={faCog} componentKey="wheels" />
                    <ComponentPanel name="Hook" icon={faLink} componentKey="hook" />
                </div>
            </div>

            <SettingsPanel />
        </div>
    );
};

export default VibrationMonitoring;